'use client';

import { intervalToDuration } from 'date-fns';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { formatCountdown } from '../../../utils/time';

import { CheckCircle, Grid, Loader } from 'phosphor-icons';
import { useEffect, useState } from 'react';
import { isFree } from '../../../utils/price';
import { Badge, BadgeProps } from '../../atoms/Badge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Price } from '../../atoms/Price/Price';
import { BadgeList } from '../../molecules/BadgeList/BadgeList';
import { PublisherInfo } from '../../molecules/PublisherInfo/PublisherInfo';
import { QuantitySelector } from '../../molecules/QuantitySelector';
import { ReviewStars } from '../../molecules/ReviewStars/ReviewStars';
import { RichText } from '../../molecules/RichText/RichText';
import {
  ProductInfoProps,
  ProductInfoPropsActions,
  ProductInfoPropsMicrocopy,
} from './ProductInfo.types';

const renderCtaButton = ({
  type,
  price,
  quantity,
  nbOfPackages,
  isEntitled,
  isPurchasing,
  microcopy,
  onProductCta,
}: Pick<
  ProductInfoProps,
  | 'type'
  | 'price'
  | 'nbOfPackages'
  | 'isEntitled'
  | 'isPurchasing'
  | 'microcopy'
> &
  Pick<ProductInfoPropsActions, 'onProductCta'> & {
    quantity: number;
    className?: string;
  }) => {
  let label: string;
  if (type === 'single-package') {
    if (isEntitled) {
      label = microcopy.openInUnity;
    } else if (!price.currentPrice) {
      label = microcopy.addToMyAssetSinglePackage;
    } else {
      label = microcopy.buyNow;
    }
  } else {
    if (isEntitled) {
      label = microcopy.seeOwnedPackages;
    } else if (!price.currentPrice) {
      label = interpolateStringWithData(microcopy.addToMyAssetMultiPackages, {
        number: nbOfPackages,
      });
    } else {
      label = microcopy.buyNow;
    }
  }

  return (
    <ButtonV2
      label={label}
      variant={
        !isEntitled
          ? 'primary'
          : !price.currentPrice
            ? 'secondary-blue-monochrome'
            : 'primary'
      }
      size="large"
      shape="square"
      onClick={() => onProductCta?.(quantity)}
      className="w-full basis-auto"
      disabled={isPurchasing}
      iconLeft={isPurchasing ? 'SpinnerGap' : undefined}
      iconWeight={isPurchasing ? 'bold' : undefined}
      iconClassName={isPurchasing ? 'animate-spin' : undefined}
      iconSize={isPurchasing ? 20 : undefined}
    />
  );
};

const renderFavoriteButton = ({
  isFavorite,
  microcopy,
  onFavorite,
}: Pick<ProductInfoProps, 'isFavorite' | 'microcopy' | 'onFavorite'>) => {
  if (isFavorite == null || !onFavorite) {
    return null;
  }

  return (
    <ButtonV2
      key={isFavorite ? 'favorite' : 'not-favorite'}
      variant={isFavorite ? 'primary' : 'secondary-blue-monochrome'}
      size="large"
      shape="square"
      iconLeft="BookmarkSimple"
      iconWeight={isFavorite ? 'fill' : 'bold'}
      iconSize={20}
      aria-label={
        isFavorite ? microcopy.removeFromFavorites : microcopy.addToFavorites
      }
      aria-pressed={isFavorite}
      className="basis-auto"
      onClick={() => onFavorite()}
    />
  );
};

const renderUpmBadgeTooltip = (content: string): React.ReactNode => (
  <RichText
    html={content}
    className="text-sm [&_a]:text-sm [&_a]:no-underline [&_p]:mb-0 [&_span]:mb-0 [&_span]:text-grey-700"
  />
);

const renderVerifiedSolutionsTooltip = (
  content: ProductInfoPropsMicrocopy['badges']['verifiedSolutions']['tooltip']
): React.ReactNode => {
  const { title, benefit1, benefit2, benefit3, cta } = content;
  const flexItemBadge = 'flex items-center';
  return (
    <>
      {title && (
        <RichText
          html={title}
          className="text-sm [&_a]:text-sm [&_a]:no-underline [&_span]:mb-0 [&_span]:text-grey-700"
        />
      )}
      {benefit1 && (
        <div className="px-3 py-2 pb-0">
          <div className={flexItemBadge}>
            <CheckCircle
              className="mr-3 mt-1 inline-block text-blue"
              size={25}
              aria-hidden="true"
            />
            <RichText
              html={benefit1}
              className="py-1.5 text-sm [&_a]:no-underline"
              type="p"
            />
          </div>
          {benefit2 && (
            <div className={flexItemBadge}>
              <div className="mr-1 pt-1">
                <Loader />
              </div>
              <RichText html={benefit2} className="py-1.5 text-sm" />
            </div>
          )}
          {benefit3 && (
            <div className={flexItemBadge}>
              <div className="mr-1 pt-1">
                <Grid />
              </div>
              <RichText html={benefit3} className="py-1.5 text-sm" />
            </div>
          )}
        </div>
      )}
      {cta && (
        <div className="mt-1 py-1">
          <RichText
            html={cta}
            className="py-1.5 text-sm [&_a]:no-underline [&_p]:mb-0"
            type="p"
          />
        </div>
      )}
    </>
  );
};

const renderEulaMessage = ({
  price,
  customEula,
  microcopy,
}: Pick<ProductInfoProps, 'price' | 'customEula' | 'microcopy'>) => {
  let html: string;
  if (price.currentPrice) {
    html = microcopy.refundPolicy;
  } else if (customEula) {
    html = interpolateStringWithData(microcopy.customEulaMessage, {
      custom: customEula,
    });
  } else {
    html = microcopy.standardEulaMessage;
  }

  const richTextProps = {
    html,
    className: 'text-xs text-grey-700 [&_p]:mb-0',
    linkComponent: ({ href, children, target, rel }) => (
      <LinkV2
        href={href}
        openInANewTab={target === '_blank'}
        variant="link-in-text-primary"
        rel={rel}
      >
        {children}
      </LinkV2>
    ),
  };
  return <RichText {...richTextProps} />;
};

export const ProductInfo = ({
  variant = 'default',
  badges,
  productTitle,
  publisherName,
  publisherLink,
  isCertifiedPublisher,
  publisherAccountType,
  publisherAvatarUrl,
  price,
  sale,
  type,
  isEntitled,
  nbOfPackages,
  microcopy,
  className = '',
  customEula,
  reviews,
  isFavorite,
  isReviewsEnabled = false,
  isPurchasing = false,
  onProductCta,
  onFavorite,
  onAddSeats,
}: ProductInfoProps) => {
  const [quantity, setQuantity] = useState(1);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!sale?.dates?.ends) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [sale?.dates?.ends]);

  const isPreviewMode = !onProductCta;

  const saleCountdownFormatted =
    sale?.dates?.ends && sale.dates.ends > now
      ? formatCountdown(
          sale.dates.ends,
          intervalToDuration({ start: now, end: sale.dates.ends }),
          {
            withDays: microcopy.sale.countdownWithDays,
            hoursOnly: microcopy.sale.countdownHoursOnly,
          }
        )
      : null;

  const badgeList: BadgeProps[] = badges.filter(Boolean).map((badge) => {
    switch (badge) {
      case 'upm':
        return {
          text: microcopy.badges.upm.text,
          popover: renderUpmBadgeTooltip(microcopy.badges.upm.tooltip ?? ''),
        };
      case 'verifiedSolutions':
        return {
          text: microcopy.badges.verifiedSolutions.text,
          popover: renderVerifiedSolutionsTooltip(
            microcopy.badges.verifiedSolutions.tooltip
          ),
        };
      case 'deprecated':
        return {
          text: microcopy.badges.deprecated.text,
          className:
            'text-red-600 bg-red-100 outline outline-1 outline-offset-0 outline-red-600',
        };
    }
  });

  return variant === 'default' ? (
    <div
      data-test={'product-info-section'}
      className={c('flex flex-col gap-y-4', className)}
    >
      {badgeList.length > 0 && <BadgeList badges={badgeList} />}

      <h2
        data-test="product-title"
        className="text-xl font-semibold leading-8 text-grey-900"
      >
        {productTitle}
      </h2>

      {isReviewsEnabled && reviews && microcopy.reviewStars && (
        <ReviewStars
          value={reviews.value}
          max={reviews.max}
          reviews={reviews.reviews}
          microcopy={microcopy.reviewStars}
          variant="small"
          showReviewsLink={true}
        />
      )}

      <PublisherInfo
        showAvatar={true}
        isCertified={isCertifiedPublisher}
        publisherAccountType={publisherAccountType}
        publisherName={publisherName}
        publisherLink={publisherLink}
        publisherAvatarUrl={publisherAvatarUrl}
        microcopy={{
          certifiedPublisher: microcopy.certifiedPublisher,
          verifiedOrganization: microcopy.verifiedOrganization,
          verifiedIndividual: microcopy.verifiedIndividual,
        }}
      />

      <div className="space-y-1">
        <div className="flex gap-2">
          <Price
            className={c(
              'text-grey-900',
              isFree(price)
                ? 'text-md font-normal leading-7 tracking-[-0.108px]'
                : 'text-xl font-medium leading-8 tracking-[-0.72px]'
            )}
            value={isFree(price) ? undefined : String(price.currentPrice)}
            currency={price.currency}
            microcopy={{ free: microcopy.free }}
          />
          {price.currentPrice !== price.originalPrice && (
            <Price
              className="text-xl font-medium leading-8 tracking-[-0.72px] text-grey-400 line-through"
              value={String(price.originalPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />
          )}
        </div>
        {(price.discountPercent || sale) && (
          <div className="flex gap-2">
            {sale?.type === 'new_release_discount' && (
              <Badge variant="default" text={microcopy.sale.newRelease} />
            )}
            {price.discountPercent && (
              <Badge variant="sale" text={`-${price.discountPercent}%`} />
            )}

            {saleCountdownFormatted && (
              <p
                suppressHydrationWarning
                className="text-xs leading-5 tracking-[-0.024px] text-grey-700"
              >
                {microcopy.sale.endsIn} {saleCountdownFormatted}
              </p>
            )}
          </div>
        )}
      </div>

      {!isPreviewMode && !isEntitled && price.currentPrice ? (
        <div className="space-y-1">
          <div className="text-sm leading-5 tracking-[-0.028px] text-grey-700">
            {microcopy.seats}
          </div>
          <QuantitySelector
            value={quantity}
            onDecreaseQuantity={() => setQuantity((q) => q - 1)}
            onIncreaseQuantity={() => setQuantity((q) => q + 1)}
            microcopy={microcopy.quantitySelector}
          />
        </div>
      ) : (
        <></>
      )}

      {isPreviewMode ? (
        <div />
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            {renderCtaButton({
              type,
              price,
              quantity,
              isEntitled,
              isPurchasing,
              nbOfPackages,
              microcopy,
              onProductCta,
            })}
            {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
          </div>
          {isEntitled && price.currentPrice ? (
            <ButtonV2
              variant="secondary-blue-monochrome"
              size="large"
              shape="square"
              className="w-full basis-auto"
              iconRight="UserPlus"
              iconClassName="w-5 h-5"
              label={microcopy.addSeats}
              onClick={onAddSeats}
            />
          ) : (
            <></>
          )}

          {!isEntitled && renderEulaMessage({ price, customEula, microcopy })}
        </div>
      )}
    </div>
  ) : (
    <div
      className={c(
        'fixed bottom-0 left-0 right-0 z-50 border-t border-grey-300 bg-grey-50 lg:fixed lg:bottom-auto lg:top-0 lg:border-b lg:border-t-0',
        className
      )}
    >
      <div className="lg-container flex w-full items-start justify-between py-3">
        <div className="hidden flex-col lg:flex">
          <h3 className="text-gray-900 text-md font-medium leading-7">
            {productTitle}
          </h3>
          <div className="flex items-center">
            <PublisherInfo
              size="small"
              showAvatar={false}
              isCertified={isCertifiedPublisher}
              publisherAccountType={publisherAccountType}
              publisherName={publisherName}
              publisherLink={publisherLink}
              publisherAvatarUrl={publisherAvatarUrl}
              microcopy={{
                certifiedPublisher: microcopy.certifiedPublisher,
                verifiedOrganization: microcopy.verifiedOrganization,
                verifiedIndividual: microcopy.verifiedIndividual,
              }}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 lg:max-w-[496px]">
          <div className="flex w-full items-center justify-between gap-4 lg:gap-8">
            <Price
              value={
                price.originalPrice ? String(price.originalPrice) : undefined
              }
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />

            {isPreviewMode ? (
              <div />
            ) : (
              <div className="flex gap-2 lg:w-full">
                {renderCtaButton({
                  type,
                  price,
                  quantity,
                  isEntitled,
                  isPurchasing,
                  nbOfPackages,
                  microcopy,
                  onProductCta,
                })}
                {renderFavoriteButton({
                  isFavorite,
                  microcopy,
                  onFavorite,
                })}
              </div>
            )}
          </div>
          {!isPreviewMode &&
            !isEntitled &&
            renderEulaMessage({ price, customEula, microcopy })}
        </div>
      </div>
    </div>
  );
};
